import { useTranslation } from '@pasteltech/react-i18n'
import { FontWeight } from '@pasteltech/theme-provider'
import { Link } from 'gatsby'
import { memo } from 'react'
import { styled } from 'styled-components'
import { PagePaths } from '../../constants'
import { ArrowForwardIcon } from '../../svg-components'
import { DeepNullable, ScreenQuery } from '../../utilities'
import { StrapiImage, StrapiImageData } from '../strapi-image'

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  margin: 0 auto;

  ${ScreenQuery.desktop} {
    width: 70%;
    padding: 300px 0px;
  }
  ${ScreenQuery.tablet} {
    flex-direction: column-reverse;
    width: 80%;
    padding: 150px 0px;
  }
  ${ScreenQuery.mobile} {
    flex-direction: column-reverse;
    width: 90%;
    padding: 70px 0px;
  }
`

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  flex: 1 0 0;
`

const StyledStrapiImage = styled(StrapiImage)`
  border-radius: ${({ theme }) => theme.sizes.borderRadius.sm}px;
`
const RightContent = styled.div`
  flex: 1 0 0;
`
const DetailWrapper = styled.div`
  display: flex;

  padding-top: ${({ theme }) => `${theme.sizes.spacings.xxxxl}px`};
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme }) => `${theme.sizes.spacings.xl}px`};
  align-self: stretch;
  border-radius: ${({ theme }) => `${theme.sizes.borderRadius.lg}px`};
`
const TeamName = styled.div`
  color: ${({ theme }) => theme.colors.text.medium};
  font-size: ${({ theme }) => theme.sizes.fontSize.title};
  font-weight: ${FontWeight.bold};
`
const Title = styled.div`
  color: ${({ theme }) => theme.colors.text.high};
  font-size: ${({ theme }) => `${theme.sizes.fontSize.heading2}px`};
  font-weight: ${FontWeight.bold};
`
const Content = styled.div`
  color: ${({ theme }) => theme.colors.text.medium};
  font-size: ${({ theme }) => `${theme.sizes.fontSize.subtitle}px`};
  font-weight: ${FontWeight.medium};
`
const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.sizes.spacings.xs}px;
  border: none;
  padding: ${({ theme }) => `${theme.sizes.spacings.lg}px`};
  background: transparent;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:active {
    opacity: 0.6;
  }
`
const ButtonText = styled.div`
  font-size: ${({ theme }) => `${theme.sizes.fontSize.button}px`};
  font-weight: ${FontWeight.bold};
  color: ${({ theme }) => theme.colors.text.high};
`
const StyledLink = styled(Link)`
  text-decoration: none;
`

type CompanyProfile = null | DeepNullable<{
  teamName: string
  title: string
  content: string
  image: StrapiImageData
}>
type Props = {
  data: CompanyProfile
}

export const CompanyProfileSection = memo(({ data }: Props) => {
  const { t } = useTranslation()
  return (
    <Container>
      <LeftContent>
        <TeamName>{data?.teamName}</TeamName>
        <DetailWrapper>
          <Title>{data?.title}</Title>
          <Content>{data?.content}</Content>
        </DetailWrapper>
        <StyledLink to={PagePaths.aboutUs}>
          <StyledButton>
            <ButtonText>{t('button.moreDetails')}</ButtonText>
            <ArrowForwardIcon />
          </StyledButton>
        </StyledLink>
      </LeftContent>
      <RightContent>
        <StyledStrapiImage strapiImage={data?.image} />
      </RightContent>
    </Container>
  )
})
CompanyProfileSection.displayName = 'CompanyProfileSection'
